import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { PaymentDetailModal } from '../components/PaymentDetailModal';
import {
  ApprovalFilterModal,
  EMPTY_FILTERS,
  countActiveFilters,
  type ApprovalFilters,
} from '../components/ApprovalFilterModal';
import { TypePill } from '../components/StatusBadge';
import { useAuth } from '../context/AuthContext';
import {
  approvalsApi,
  type ApprovalItem,
  type ApprovalTab,
} from '../lib/api/approvals';
import { paymentsApi, type PaymentRequest, type PaymentType } from '../lib/api/payments';
import { formatVND } from '../lib/format';

const TYPE_LABELS: Record<PaymentType, string> = {
  chi_thuong: 'Thanh toán',
  tam_ung: 'Tạm ứng',
  ung_luong: 'Ứng lương',
  hoan_ung: 'Hoàn ứng',
  thuong_nong: 'Thưởng nóng',
};

const CEO_THRESHOLD = 3_000_000;
const PAGE_SIZE = 20;

export function ApprovalsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [tabCounts, setTabCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [counts, setCounts] = useState({ urgent: 0, batch: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<ApprovalTab>('pending');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<PaymentRequest | null>(null);
  const [busyBulk, setBusyBulk] = useState(false);

  const openDetailFromApproval = useCallback(async (a: ApprovalItem) => {
    // Show ngay data từ list (fast) — sau đó fetch full detail (có cashVoucher + history) → replace.
    setDetail(a.paymentRequest);
    try {
      const full = await paymentsApi.get(a.paymentRequestId);
      setDetail(full);
    } catch {
      /* giữ data cơ bản nếu fetch full lỗi */
    }
  }, []);

  const [filters, setFilters] = useState<ApprovalFilters>(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilterCount = countActiveFilters(filters);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await approvalsApi.list({
        tab,
        search: debouncedSearch || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setItems(res.items);
      setCounts({ urgent: res.urgentCount, batch: res.batchCount });
      setTotal(res.paging.total);
      if (res.tabCounts) setTabCounts(res.tabCounts);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [tab, debouncedSearch, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset page khi đổi tab / search
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [tab, debouncedSearch]);

  const filteredItems = useMemo(() => {
    return items.filter((a) => {
      const pr = a.paymentRequest;
      if (filters.types.length > 0 && !filters.types.includes(pr.type)) return false;
      if (filters.queues.length > 0 && !filters.queues.includes(a.queueType)) return false;
      if (filters.needCeo !== null) {
        const needsCeo = pr.amount >= 3_000_000;
        if (filters.needCeo !== needsCeo) return false;
      }
      return true;
    });
  }, [items, filters]);

  const urgentRows = useMemo(
    () => (tab === 'pending' ? filteredItems.filter((a) => a.queueType === 'urgent') : []),
    [filteredItems, tab],
  );
  const batchRows = useMemo(
    () => (tab === 'pending' ? filteredItems.filter((a) => a.queueType === 'batch') : []),
    [filteredItems, tab],
  );
  const flatRows = useMemo(() => (tab === 'pending' ? [] : filteredItems), [filteredItems, tab]);

  const toggleSel = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    const pendingIds = tab === 'pending' ? items.filter((a) => a.status === 'PENDING').map((a) => a.id) : [];
    if (selected.size === pendingIds.length && pendingIds.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingIds));
    }
  };
  const clearSel = () => setSelected(new Set());
  const selArr = Array.from(selected);
  const selItems = items.filter((i) => selected.has(i.id));
  const selTotal = selItems.reduce((a, i) => a + i.paymentRequest.amount, 0);

  const handleBulkApprove = async () => {
    if (selArr.length === 0) return;
    if (!confirm(`Duyệt ${selArr.length} phiếu · Tổng ${formatVND(selTotal)}?`)) return;
    setBusyBulk(true);
    try {
      const res = await approvalsApi.bulkApprove(selArr);
      if (res.failed.length > 0) {
        alert(`Duyệt ${res.approved} thành công · ${res.failed.length} thất bại`);
      }
      clearSel();
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setBusyBulk(false);
    }
  };

  const handleBulkReject = () => {
    if (selArr.length === 0) return;
    const reason = prompt(`Từ chối ${selArr.length} phiếu — nhập lý do (≥10 ký tự):`);
    if (!reason || reason.length < 10) return;
    (async () => {
      for (const id of selArr) {
        try {
          await approvalsApi.reject(id, reason);
        } catch {
          /* skip */
        }
      }
      clearSel();
      await load();
    })();
  };

  const handleActionDone = async () => {
    setDetail(null);
    await load();
  };

  if (user?.role !== 'KTT' && user?.role !== 'CEO') {
    return (
      <AppShell title="Trung tâm phê duyệt" subtitle="Chỉ KTT hoặc CEO xem được trang này">
        <div className="pay-page-card">
          <div className="pay-empty">
            <div className="pay-empty-icon">🔒</div>
            <div>Bạn không có quyền xem trung tâm phê duyệt.</div>
          </div>
        </div>
      </AppShell>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showTo = Math.min(page * PAGE_SIZE, total);

  return (
    <AppShell
      title="Trung tâm phê duyệt"
      subtitle={
        user.role === 'CEO'
          ? 'Ký duyệt cuối các phiếu chi ≥ 3.000.000đ đã qua KTT'
          : 'KTT duyệt các đề nghị chi theo 2 luồng: gấp trong ngày · batch T6 hàng tuần'
      }
    >
      <div className="pay-page-card apr-page">
        {/* TOP ROW: search + filter */}
        <div className="apr-top-row">
          <div className="apr-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input
              placeholder="Tìm mã đề nghị, người tạo, diễn giải..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="pay-btn-outline" onClick={() => setFilterOpen(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" x2="14" y1="4" y2="4" /><line x1="10" x2="3" y1="4" y2="4" />
              <line x1="21" x2="12" y1="12" y2="12" /><line x1="8" x2="3" y1="12" y2="12" />
              <line x1="21" x2="16" y1="20" y2="20" /><line x1="12" x2="3" y1="20" y2="20" />
              <line x1="14" x2="14" y1="2" y2="6" /><line x1="8" x2="8" y1="10" y2="14" /><line x1="16" x2="16" y1="18" y2="22" />
            </svg>
            Bộ lọc
            {activeFilterCount > 0 && <span className="apr-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>

        {/* ACTIVE FILTER CHIPS */}
        {activeFilterCount > 0 && (
          <div className="apr-filter-chips">
            {filters.types.map((t) => (
              <span key={t} className="apr-filter-chip">
                Loại: {TYPE_LABELS[t]}
                <button
                  onClick={() =>
                    setFilters((f) => ({ ...f, types: f.types.filter((x) => x !== t) }))
                  }
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </span>
            ))}
            {filters.queues.map((q) => (
              <span key={q} className="apr-filter-chip">
                Ưu tiên: {q === 'urgent' ? 'Khẩn' : 'Bình thường'}
                <button
                  onClick={() =>
                    setFilters((f) => ({ ...f, queues: f.queues.filter((x) => x !== q) }))
                  }
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </span>
            ))}
            {filters.needCeo !== null && (
              <span className="apr-filter-chip">
                {filters.needCeo ? 'Cần CEO ký' : 'KTT duyệt cuối'}
                <button onClick={() => setFilters((f) => ({ ...f, needCeo: null }))}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            <button className="apr-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)}>
              Xoá tất cả
            </button>
          </div>
        )}

        {/* SELECTED BAR */}
        {selected.size > 0 && (
          <div className="apr-selected-bar">
            <div className="apr-selected-info">
              Đã chọn <b>{selected.size} mục</b> · Tổng <b>{formatVND(selTotal)}</b>
            </div>
            <div className="apr-selected-actions">
              <button className="apr-btn-reject-outline" onClick={handleBulkReject}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></svg>
                Từ chối
              </button>
              <button className="apr-btn-approve-solid" onClick={handleBulkApprove} disabled={busyBulk}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                {busyBulk ? 'Đang duyệt…' : 'Duyệt hàng loạt'}
              </button>
              <button className="apr-btn-clear" onClick={clearSel}>Bỏ chọn</button>
            </div>
          </div>
        )}

        {/* TABS */}
        <div className="apr-tabs">
          <TabButton active={tab === 'pending'} onClick={() => setTab('pending')} label="Chờ tôi duyệt" count={tabCounts.pending} />
          <TabButton active={tab === 'approved'} onClick={() => setTab('approved')} label="Đã duyệt" count={tabCounts.approved} />
          <TabButton active={tab === 'rejected'} onClick={() => setTab('rejected')} label="Đã từ chối" count={tabCounts.rejected} />
          <TabButton active={tab === 'all'} onClick={() => setTab('all')} label="Tất cả" count={tabCounts.all} />
        </div>

        {error && <div className="pay-table-error">⚠️ {error}</div>}

        {/* TABLE */}
        <div className="apr-table-wrap">
          <table className="apr-table">
            <colgroup>
              <col style={{ width: 40 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 200 }} />
              <col />
              <col style={{ width: 140 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 60 }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === items.filter((a) => a.status === 'PENDING').length}
                    onChange={toggleAll}
                    disabled={tab !== 'pending'}
                  />
                </th>
                <th>Mã đề nghị</th>
                <th>Loại</th>
                <th>Người đề nghị</th>
                <th>Diễn giải</th>
                <th style={{ textAlign: 'right' }}>Số tiền</th>
                <th>Chờ duyệt</th>
                <th>Ưu tiên</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="apr-td-empty">Đang tải…</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="apr-td-empty">
                    <div style={{ padding: '32px 16px' }}>
                      {tab === 'pending' ? '✨ Không có phiếu nào chờ bạn duyệt' : 'Không có dữ liệu'}
                    </div>
                  </td>
                </tr>
              ) : tab === 'pending' ? (
                <>
                  {urgentRows.length > 0 && <SectionHeaderRow tone="urgent" count={counts.urgent} />}
                  {urgentRows.map((a) => (
                    <ApprovalRow
                      key={a.id}
                      approval={a}
                      selected={selected.has(a.id)}
                      onToggle={toggleSel}
                      onOpen={openDetailFromApproval}
                      currentRole={user.role as 'KTT' | 'CEO'}
                      showCheckbox
                    />
                  ))}
                  {batchRows.length > 0 && <SectionHeaderRow tone="batch" count={counts.batch} />}
                  {batchRows.map((a) => (
                    <ApprovalRow
                      key={a.id}
                      approval={a}
                      selected={selected.has(a.id)}
                      onToggle={toggleSel}
                      onOpen={openDetailFromApproval}
                      currentRole={user.role as 'KTT' | 'CEO'}
                      showCheckbox
                    />
                  ))}
                </>
              ) : (
                flatRows.map((a) => (
                  <ApprovalRow
                    key={a.id}
                    approval={a}
                    selected={false}
                    onToggle={toggleSel}
                    onOpen={openDetailFromApproval}
                    currentRole={user.role as 'KTT' | 'CEO'}
                    showCheckbox={false}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {total > 0 && (
          <div className="apr-pagination">
            <div className="apr-pag-info">Hiển thị {showFrom}-{showTo} của {total} đề nghị</div>
            <div className="apr-pag-nav">
              <button
                className="apr-pag-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ◀ Trước
              </button>
              <button className="apr-pag-btn apr-pag-current">{page}</button>
              <button
                className="apr-pag-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Sau ▶
              </button>
            </div>
          </div>
        )}
      </div>

      <PaymentDetailModal
        open={!!detail}
        data={detail}
        onClose={() => setDetail(null)}
        onActionDone={handleActionDone}
      />

      <ApprovalFilterModal
        open={filterOpen}
        initial={filters}
        onClose={() => setFilterOpen(false)}
        onApply={(f) => setFilters(f)}
      />
    </AppShell>
  );
}

function TabButton({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button className={`apr-tab ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
      <span className="apr-tab-badge">{count}</span>
    </button>
  );
}

function SectionHeaderRow({ tone, count }: { tone: 'urgent' | 'batch'; count: number }) {
  const isUrgent = tone === 'urgent';
  return (
    <tr className={`apr-section-row apr-section-row-${tone}`}>
      <td colSpan={9}>
        <div className="apr-section-inner">
          <div className={`apr-section-icon ${isUrgent ? 'urgent' : 'batch'}`}>
            {isUrgent ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <rect width="18" height="18" x="3" y="4" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" />
              </svg>
            )}
          </div>
          <div>
            <div className="apr-section-title">
              {isUrgent ? `Duyệt ngay trong ngày · ${count} phiếu` : `Duyệt định kỳ thứ 6 · ${count} phiếu`}
            </div>
            <div className="apr-section-sub">
              {isUrgent
                ? 'Chi khẩn cấp · tạm ứng đột xuất — cần KTT/CEO xử lý trong 24h'
                : 'Chi thường · tạm ứng có kế hoạch — batch weekly, xử lý cuối tuần'}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

function ApprovalRow({
  approval: a,
  selected,
  onToggle,
  onOpen,
  currentRole,
  showCheckbox,
}: {
  approval: ApprovalItem;
  selected: boolean;
  onToggle: (id: string) => void;
  onOpen: (a: ApprovalItem) => void;
  currentRole: 'KTT' | 'CEO';
  showCheckbox: boolean;
}) {
  const pr = a.paymentRequest;
  const needCeo = currentRole === 'KTT' && pr.amount >= CEO_THRESHOLD;
  const initials = (pr.requesterName || '?')
    .split(' ')
    .slice(-2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  // Calculate wait time
  const waitDays = Math.floor(
    (Date.now() - new Date(pr.submittedAt || pr.createdAt).getTime()) / 86400000,
  );
  const waitLabel = waitDays === 0 ? 'Hôm nay' : waitDays === 1 ? '1 ngày' : `${waitDays} ngày`;
  const waitColor = waitDays >= 3 ? '#dc2626' : waitDays >= 1 ? '#a16207' : '#64748b';

  return (
    <tr
      className={`apr-row ${selected ? 'selected' : ''}`}
      onClick={() => onOpen(a)}
    >
      <td onClick={(e) => e.stopPropagation()}>
        {showCheckbox && a.status === 'PENDING' && (
          <input type="checkbox" checked={selected} onChange={() => onToggle(a.id)} />
        )}
      </td>
      <td className="apr-td-code">{pr.code}</td>
      <td>
        <TypePill type={pr.type} advanceType={pr.advanceType} />
      </td>
      <td>
        <div className="apr-td-requester">
          <div className="apr-td-avatar">{initials}</div>
          <div className="apr-td-req-meta">
            <div className="apr-td-req-name">{pr.requesterName || pr.requesterId}</div>
            <div className="apr-td-req-dept">{pr.department || '—'}</div>
          </div>
        </div>
      </td>
      <td>
        <div className="apr-td-reason" title={pr.reason}>{pr.reason}</div>
      </td>
      <td className="apr-td-amount">{formatVND(pr.amount)}</td>
      <td style={{ color: waitColor, fontSize: 12, fontWeight: 500 }}>{waitLabel}</td>
      <td>
        <div className="apr-td-priority">
          {a.queueType === 'urgent' && (
            <span className="apr-badge-khan">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              </svg>
              Khẩn
            </span>
          )}
          {needCeo && (
            <span className="apr-badge-ceo">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 2 3 7v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7Z" />
              </svg>
              CEO
            </span>
          )}
        </div>
      </td>
      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'right' }}>
        <button className="apr-row-menu" title="Thao tác">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
          </svg>
        </button>
      </td>
    </tr>
  );
}
